import React, { useMemo, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Zap, Check, AlertTriangle, MapPin } from 'lucide-react';
import { useGlobalFontSize } from '../../context/GlobalFontSizeContext';
import { useUser } from '../../context/UserContext';
import {
  useGetDefaultAlerts,
  useGetCustomAlerts,
  useGetDefaultAlertsNotifications,
  useGetCustomAlertsNotifications,
  getInitialSelectedWeeks,
  getCurrentWeekNumber,
} from '../../utils/api-hooks/alerts';
import AlertCreateModal from './AlertCreateModal';
import AlertsEnabled from './AlertsEnabled';
import AlertsOnboarding from './AlertsOnboarding';
import TriggeredAlertsSection from './TriggeredAlertsSection';
import PageSkeleton from '../../components/PageSkeleton';

// Temporary filter: hide the mock Recommendations tab in prod until it is
// wired to a backend. Dev keeps it via VITE_ENABLE_ALERT_RECOMMENDATIONS=true.
const ENABLE_ALERT_RECOMMENDATIONS =
  import.meta.env.VITE_ENABLE_ALERT_RECOMMENDATIONS === 'true';

const AlertsRecommendations = ENABLE_ALERT_RECOMMENDATIONS
  ? React.lazy(() => import('./AlertsRecommendations'))
  : null;

const HUB_TABS = [
  { id: 'overview', labelKey: 'alerts.hub.tabOverview', defaultLabel: 'Alerts overview' },
  ...(ENABLE_ALERT_RECOMMENDATIONS
    ? [
        {
          id: 'recommendations',
          labelKey: 'alerts.hub.tabRecommendations',
          defaultLabel: 'Recommendations',
        },
      ]
    : []),
];

const HubTabSelector = ({ activeTab, onChange }) => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();

  return (
    <div className="flex justify-center">
      <div className="inline-flex overflow-hidden rounded-[10px] border border-[#E3E5E4] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d] p-1">
        {HUB_TABS.map((tab) => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onChange(tab.id)}
              className={`rounded-[8px] px-4 sm:px-5 py-2 font-['Inter'] font-medium transition-colors ${
                active
                  ? 'bg-bloom-green text-white'
                  : 'text-[#5D625F] dark:text-[#C3C4C3] hover:text-[#282A29] dark:hover:text-white'
              }`}
              style={{ fontSize }}
            >
              {t(tab.labelKey, tab.defaultLabel)}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const OverviewCard = ({ icon: Icon, iconClass, title, value, subtitle, subtitleClass = '' }) => {
  const { fontSize } = useGlobalFontSize();
  return (
    <div className="flex flex-col gap-3 rounded-[8px] border border-[#C3C4C3] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d] px-4 py-4 min-h-[120px]">
      <div className="flex items-center gap-2">
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full ${iconClass}`}>
          <Icon size={16} />
        </span>
        <p
          className="font-['Inter'] font-medium text-[#5D625F] dark:text-[#C3C4C3]"
          style={{ fontSize: fontSize - 1 }}
        >
          {title}
        </p>
      </div>
      <p
        className="font-['Inter'] font-semibold text-[#282A29] dark:text-white leading-none"
        style={{ fontSize: fontSize + 14 }}
      >
        {value}
      </p>
      {subtitle ? (
        <p
          className={`font-['Inter'] text-[#828382] dark:text-[#888] ${subtitleClass}`}
          style={{ fontSize: fontSize - 2 }}
        >
          {subtitle}
        </p>
      ) : null}
    </div>
  );
};

const CreateAlertBanner = ({ onCreate }) => {
  const { t } = useTranslation();
  const { fontSize } = useGlobalFontSize();

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-[8px] border border-[#C3C4C3] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d] px-5 py-4">
      <div className="min-w-0">
        <h2
          className="font-['Inter'] font-semibold text-[#282A29] dark:text-white"
          style={{ fontSize: fontSize + 4 }}
        >
          {t('alerts.hub.createTitle')}
        </h2>
        <p
          className="mt-1 font-['Inter'] text-[#5D625F] dark:text-[#C3C4C3]"
          style={{ fontSize }}
        >
          {t('alerts.hub.createSubtitle')}
        </p>
      </div>
      <button
        type="button"
        onClick={onCreate}
        className="shrink-0 rounded-[8px] bg-bloom-green px-5 py-2.5 font-['Inter'] font-medium text-white hover:bg-bloom-green-hover transition-colors"
        style={{ fontSize }}
      >
        {t('alerts.hub.createButton')}
      </button>
    </div>
  );
};

const AlertsOverview = () => {
  const { t } = useTranslation();

  const { data: defaultAlertsData } = useGetDefaultAlerts();
  const { data: customAlertsData } = useGetCustomAlerts();
  const lastFourWeeks = useMemo(() => getInitialSelectedWeeks(), []);
  const { data: defaultNotificationsData } = useGetDefaultAlertsNotifications(lastFourWeeks);
  const { data: customNotificationsData } = useGetCustomAlertsNotifications(lastFourWeeks);

  const metrics = useMemo(() => {
    const defaultAlerts = defaultAlertsData?.alerts || [];
    const customAlerts = customAlertsData?.alerts || [];
    const allEnabled = [...defaultAlerts, ...customAlerts];
    const enabledCount = allEnabled.filter((a) => a.enabled !== false).length;
    const commodities = new Set(
      allEnabled.map((a) => (a.commodity || '').toLowerCase()).filter(Boolean)
    );

    const regions = new Set();
    allEnabled.forEach((a) => {
      if (Array.isArray(a.regions)) {
        a.regions.forEach((r) => {
          const name = typeof r === 'string' ? r : r?.country || r?.name;
          if (name && String(name).toLowerCase() !== 'recommended') regions.add(String(name));
        });
      } else if (a.regions && typeof a.regions === 'object') {
        Object.keys(a.regions).forEach((name) => {
          if (name && name.toLowerCase() !== 'recommended') regions.add(String(name));
        });
      }
    });

    const uniqueTriggered = [
      ...(defaultNotificationsData?.alerts || []),
      ...(customNotificationsData?.alerts || []),
    ].filter(
      (alert, index, arr) =>
        arr.findIndex((other) => other.unique_key === alert.unique_key) === index
    );
    const currentWeek = getCurrentWeekNumber();
    const thisWeekCount = uniqueTriggered.filter((a) => Number(a.week) === currentWeek).length;
    const highImpact = uniqueTriggered.filter((a) =>
      ['high', 'alto'].includes(String(a.market_impact || '').toLowerCase())
    ).length;

    const regionCount =
      regions.size > 0
        ? regions.size
        : allEnabled.reduce((sum, a) => sum + (Number(a.number_of_regions) || 0), 0);

    return {
      triggeredCount: uniqueTriggered.length,
      thisWeekCount,
      enabledCount,
      commodityCount: commodities.size,
      highImpact,
      regionCount,
      regionNames: [...regions].slice(0, 5),
    };
  }, [defaultAlertsData, customAlertsData, defaultNotificationsData, customNotificationsData]);

  return (
    <section className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
        <OverviewCard
          icon={Zap}
          iconClass="bg-bloom-green/15 text-bloom-green"
          title={t('alerts.hub.triggeredTitle')}
          value={metrics.triggeredCount}
          subtitle={t('alerts.hub.triggeredSubtitle', { count: metrics.thisWeekCount })}
          subtitleClass="text-bloom-green"
        />
        <OverviewCard
          icon={Check}
          iconClass="bg-bloom-green/15 text-bloom-green"
          title={t('alerts.hub.enabledTitle')}
          value={metrics.enabledCount}
          subtitle={t('alerts.hub.enabledSubtitle', { count: metrics.commodityCount })}
        />
        <OverviewCard
          icon={AlertTriangle}
          iconClass="bg-red-500/15 text-red-500"
          title={t('alerts.hub.attentionTitle')}
          value={metrics.highImpact}
          subtitle={t('alerts.hub.attentionSubtitle')}
          subtitleClass="text-red-500"
        />
        <OverviewCard
          icon={MapPin}
          iconClass="bg-blue-500/15 text-blue-500"
          title={t('alerts.hub.regionsTitle')}
          value={metrics.regionCount}
          subtitle={
            metrics.regionNames.length > 0
              ? `— ${metrics.regionNames.join(' · ')}`
              : t('alerts.hub.regionsSubtitle')
          }
        />
      </div>
    </section>
  );
};

const AlertsHub = () => {
  const location = useLocation();
  const { email, userDetails, firebaseUser } = useUser();
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const [forceOnboarding, setForceOnboarding] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const {
    data: defaultAlertsData,
    isLoading: isLoadingDefault,
  } = useGetDefaultAlerts();
  const {
    data: customAlertsData,
    isLoading: isLoadingCustom,
  } = useGetCustomAlerts();

  const isLoadingAlerts = isLoadingDefault || isLoadingCustom;
  const totalAlerts =
    (defaultAlertsData?.alerts?.length || 0) + (customAlertsData?.alerts?.length || 0);

  const dismissKey = email ? `alertsOnboardingDismissed:${email}` : null;

  useEffect(() => {
    if (!dismissKey) return;
    try {
      if (localStorage.getItem(dismissKey) === '1') {
        setOnboardingDismissed(true);
      }
    } catch {
      // ignore storage access issues
    }
  }, [dismissKey]);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace('#', '');
    // Deep-links into overview sections should land on the overview tab
    if (id === 'triggered' || id === 'enabled') {
      setActiveTab('overview');
    }
    const el = document.getElementById(id);
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.hash]);

  const openOnboarding = () => {
    if (dismissKey) {
      try {
        localStorage.removeItem(dismissKey);
      } catch {
        // ignore storage access issues
      }
    }
    setOnboardingDismissed(false);
    setForceOnboarding(true);
  };

  const dismissOnboarding = () => {
    setOnboardingDismissed(true);
    setForceOnboarding(false);
    if (dismissKey) {
      try {
        localStorage.setItem(dismissKey, '1');
      } catch {
        // ignore storage access issues
      }
    }
  };

  const userName =
    userDetails?.name ||
    userDetails?.first_name ||
    (firebaseUser?.displayName ? firebaseUser.displayName.split(' ')[0] : '');

  const showOnboarding =
    forceOnboarding || (!isLoadingAlerts && totalAlerts === 0 && !onboardingDismissed);

  if (isLoadingAlerts) {
    return <PageSkeleton shape="alerts" />;
  }

  if (showOnboarding) {
    return (
      <AlertsOnboarding
        userName={userName}
        onComplete={dismissOnboarding}
      />
    );
  }

  return (
    <div className="flex flex-col min-h-full w-full px-[16px] lg:px-[32px] pt-[24px] pb-[24px] bg-[#F0F5F2] bg-dark-app">
      {isCreateModalOpen && (
        <AlertCreateModal onClose={() => setIsCreateModalOpen(false)} />
      )}
      {/* TODO: remove — temporary dev entry to onboarding */}
      <button
        type="button"
        onClick={openOnboarding}
        className="fixed bottom-4 right-4 z-50 rounded-[8px] border border-dashed border-bloom-green bg-white dark:bg-[#1d1d1d] px-3 py-2 font-['Inter'] text-[12px] font-medium text-[#12823F] shadow-md hover:bg-[#E9FBF0] transition-colors"
      >
        Open onboarding (temp)
      </button>
      <div className="flex w-full flex-col gap-6 sm:gap-8">
        {HUB_TABS.length > 1 ? (
          <HubTabSelector activeTab={activeTab} onChange={setActiveTab} />
        ) : null}

        {activeTab === 'recommendations' && AlertsRecommendations ? (
          <React.Suspense fallback={null}>
            <AlertsRecommendations />
          </React.Suspense>
        ) : (
          <>
            <CreateAlertBanner onCreate={() => setIsCreateModalOpen(true)} />
            <AlertsOverview />
            <section id="triggered" className="scroll-mt-6">
              <TriggeredAlertsSection />
            </section>
            <section id="enabled" className="scroll-mt-6">
              <AlertsEnabled embedded onCreateAlert={() => setIsCreateModalOpen(true)} />
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default AlertsHub;
